/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/default-props-match-prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import ProgressLine from './ProgressLine';

import { CheckedBadgeIcon } from '../Icons';

import s from './GaugeWithTable.module.scss';
import type {
  GaugeWithTableType,
  GaugeWithTablePieDataType,
} from '../../types/components';

interface GaugeLIDChartProps {
  label: string;
  subLabel: string;
  data: GaugeWithTableType | null;
}

const colorsPalette = {
  yellow: ['#F9A31D', '#EBEEF2'],
  green: ['#6CB073', '#EBEEF2'],
  red: ['#ED5050', '#EBEEF2'],
};

const colorsStatsPalette = {
  yellow: 'rgba(249, 163, 29, 1)',
  green: 'rgba(82, 179, 107, 1)',
  red: 'rgba(244, 106, 117, 1)',
};

const statsColors = {
  yellow: 'rgba(255, 248, 237, 1)',
  green: 'rgba(239, 249, 247, 1)',
  red: 'rgba(254, 242, 243, 1)',
};

const getChartOptions = (
  value: number,
  pieData: GaugeWithTablePieDataType[],
  color: keyof typeof colorsPalette
) => ({
  fontFamily: 'Open Sans',
  tooltip: {
    trigger: 'item',
    backgroundColor: '#282828',
    borderColor: '#282828',
    textStyle: {
      color: '#FFFFFF',
    },
    padding: 16,
    formatter(params: any) {
      const itemName = params.data.name;
      const colorSpan = (color: any) =>
        `<span style="display:inline-block;margin-right:5px;border:1px solid #fff;border-radius:10px;width:9px;height:9px;background-color:${color}"></span>`;
      return `<p style="padding:0;margin:0">${colorSpan(
        params.color
      )}${itemName}: ${params.data.value.toFixed(2)}% </p>`;
    },
  },
  label: {
    fontSize: 28,
    fontFamily: 'Open Sans',
    fontWeight: 600,
    fontStyle: 'normal',
    color: colorsPalette[color][0],
    formatter: `${value.toFixed(2)}%`,
  },
  color: colorsPalette[color],
  series: [
    {
      type: 'pie',
      radius: ['60%', '73%'],
      avoidLabelOverlap: false,
      legendHoverLink: false,
      center: ['40%', '50%'],
      labelLayout: {
        hideOverlap: true,
      },
      label: {
        normal: {
          width: '96',
          border: 100,
          show: true,
          position: 'center',
          formatter: `${value.toFixed(2)}%`,
          rich: {
            a: {
              fontFamily: 'Open Sans',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '12px',
              color: '#f35461',
            },
          },
        },
        emphasis: {
          focus: 'series',
          blurScope: 'coordinateSystem',
          width: '96',
          backgroundColor: 'rgba(255, 255, 255, 1)',
          show: true,
          formatter: (params: { value: number }) =>
            `${params.value.toFixed(2)}%`,
          rich: {
            a: {
              fontFamily: 'Open Sans',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '12px',
              color: '#7BD5A7',
            },
          },
        },
      },
      itemStyle: {
        normal: {
          label: {
            show: false,
          },
        },
        emphasis: {
          label: {
            show: true,
          },
        },
      },
      emphasis: {
        label: {
          show: false,
          fontSize: '10',
          fontWeight: 'bold',
        },
      },
      labelLine: {
        show: false,
      },
      data: pieData,
    },
  ],
});

export const GaugeWithTable: FC<GaugeLIDChartProps> = ({
  label,
  subLabel,
  data,
}) => {
  if (data === null) {
    return (
      <div className={s.gauge__wrapper} style={{ minHeight: '408px' }}>
        {' '}
        <div className={s.gauge__wrapper__label}>
          <h3 className={s.gauge__wrapper__label__text}>{label}</h3>
        </div>
        <div className={s.wrapper}>
          <div className={s.empty}>
            <h3 className={s.empty__title}>Upload AIRES Files</h3>
            <div className={s.empty__margin} />
            <p className={s.empty__text}>
              Upload your AIRES files to view data related to the number of
              unique depository account holders
            </p>
          </div>
        </div>
        <div />
      </div>
    );
  }
  const { pieData, stats, table } = data;
  const {
    value,
    statsLabelPrimary,
    statsValuePrimary,
    statsLabelSecondary,
    statsValueSecondary,
    color = 'green',
  } = stats;

  // add additional emphasis config for 'No' slice, to prevent it from being highlighted as 'white'
  const modifiedPieData = pieData.map((it) => {
    if (it.name === 'No') {
      return {
        ...it,
        emphasis: {
          itemStyle: {
            color: '#EBEEF2',
          },
        },
      };
    }

    return it;
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const option = useMemo(
    () => getChartOptions(value, modifiedPieData, color),
    [value, modifiedPieData, color]
  );

  return (
    <div className={s.gauge__wrapper}>
      <div className={s.gauge__wrapper__label}>
        <h3 className={s.gauge__wrapper__label__text}>{label}</h3>
        <h5 className={s.gauge__wrapper__label__subText}>{subLabel}</h5>
      </div>
      <div className={s.wrapper}>
        <div className={s.wrapper__chart}>
          <div className={s.chart__wrapper}>
            <div className="col-sm-6" style={{ padding: 0 }}>
              <ReactECharts className={s.chart__wrapper__pie} option={option} />
            </div>
          </div>

          <div className={s.line} />
          <div
            style={{ backgroundColor: statsColors[color] }}
            className={`${s.gauge__stats} ${s.gauge__stats__LID}`}
          >
            <div>
              <h4 className={s.gauge__stats__LID__label}>
                {statsLabelPrimary}
              </h4>
              <h2
                className={s.gauge__stats__LID__value}
                style={{ color: colorsStatsPalette[color] }}
              >
                {statsValuePrimary}
              </h2>
            </div>
            <div>
              <h4 className={s.gauge__stats__LID__label}>
                {statsLabelSecondary}
              </h4>
              <h2
                className={s.gauge__stats__LID__value}
                style={{ color: colorsStatsPalette[color] }}
              >
                {statsValueSecondary}
              </h2>
            </div>
          </div>
        </div>
        <div className={s.wrapper__table}>
          <div className={s.DetailsStats}>
            <div className={s.DetailsStats__header}>
              <div className={s.DetailsStats__header__category}>
                {' '}
                {table.headers[0]}
              </div>
              <div className={s.DetailsStats__header__status}>
                {' '}
                {table.headers[1]}
              </div>
              <div className={s.DetailsStats__header__value}>
                {' '}
                {table.headers[2]}
              </div>
              <div className={s.DetailsStats__header__percentage}>
                {table.headers[3]}
              </div>
            </div>
            {table.data.map((item) => (
              <div key={`${item.name} ${item.status}`}>
                <div className={s.DetailsStats__body__line} />
                <div className={s.DetailsStats__body}>
                  <div className={s.DetailsStats__body__category}>
                    <p
                      className={s.DetailsStats__body__category__text}
                      title={item?.name}
                    >
                      {item.name}
                      {item.isVerified && (
                        <CheckedBadgeIcon
                          width={14}
                          height={14}
                          backgroundColor="#2b39b9"
                        />
                      )}
                    </p>
                  </div>
                  <div className={s.DetailsStats__body__status}>
                    <span
                      className={
                        item.status === 'No'
                          ? s.DetailsStats__body__status__no
                          : s.DetailsStats__body__status__yes
                      }
                    />{' '}
                    {item.status}
                  </div>
                  <div className={s.DetailsStats__body__value}>
                    {item.value}
                  </div>
                  <div className={s.DetailsStats__body__percentage}>
                    {item.status !== 'N/A' && (
                      <>
                        {`${item.percentage?.toFixed(2)}%`}
                        <div
                          className={s.DetailsStats__body__percentage__margin}
                        />
                        <ProgressLine
                          progressValue={item.percentage as number}
                          status={item.status as 'Yes' | 'No'}
                        />
                      </>
                    )}
                  </div>
                </div>
                {item.subCategories && (
                  <div className={s.DetailsStats__subcategories}>
                    {item.subCategories.map((subItem) => (
                      <div
                        key={`${subItem.name} ${subItem.status}`}
                        className={s.DetailsStats__body}
                        style={{ paddingLeft: '24px', marginLeft: '-24px' }}
                      >
                        <div className={s.DetailsStats__body__category}>
                          <p
                            className={s.DetailsStats__body__category__text}
                            title={subItem?.name}
                            style={{ fontSize: '12px' }}
                          >
                            {subItem.name}
                            {item.isVerified && (
                              <CheckedBadgeIcon
                                width={14}
                                height={14}
                                backgroundColor="#2b39b9"
                              />
                            )}
                          </p>
                        </div>
                        <div className={s.DetailsStats__body__status}>
                          <span
                            className={
                              subItem.status === 'No'
                                ? s.DetailsStats__body__status__no
                                : s.DetailsStats__body__status__yes
                            }
                          />{' '}
                          {subItem.status}
                        </div>
                        <div className={s.DetailsStats__body__value}>
                          {subItem.value}
                        </div>
                        <div className={s.DetailsStats__body__percentage}>
                          {subItem.status !== 'N/A' && (
                            <>
                              {`${subItem.percentage?.toFixed(2)}%`}
                              <div
                                className={
                                  s.DetailsStats__body__percentage__margin
                                }
                              />
                              <ProgressLine
                                progressValue={subItem.percentage as number}
                                status={subItem.status as 'Yes' | 'No'}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
